import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useContext, useEffect } from 'react'
import { toast } from 'sonner'
import { ClientEntryContext } from '../utils/context/clientEntry.context'
import { useCardMethodSelectedNotifications } from '../utils/websockets/useCardMethodSelectedNotifications'
import { useCardMethodVerifiedNotifications } from '../utils/websockets/useCardMethodVerifiedNotifications'
import { useOpenDoorNotifications } from '../utils/websockets/useOpenDoorNotifications'
import { usePreAuthorizationMethodSelectedNotifications } from '../utils/websockets/usePreAuthorizationMethodSelectedNotifications'
import { usePreAuthMethodVerifiedNotifications } from '../utils/websockets/usePreAuthMethodVerifiedNotifications'
import KioskToast from './KioskToast'

interface WebSocketEventData {
  storeId?: string | number
  kioskId?: string | number
  storeLabel?: string
}

const NOTIFICATION_CONFIGS = {
  doorOpen: {
    color: '#ffa400', // --secondary
    backgroundColor: '#fef9f0', // --warning-background
    duration: Infinity,
    getMessage: (storeLabel: string) =>
      `Nouvelle demande d'ouverture dans la boutique "${storeLabel}".`,
  },
  cardMethodSelected: {
    color: '#6a9560', // --primary
    backgroundColor: '#f0fdf4', // --success-background
    duration: 10_000,
    getMessage: (storeLabel: string) =>
      `Un client s'apprête à faire valider sa carte dans "${storeLabel}".`,
  },
  preAuthMethodSelected: {
    color: '#785700', // --warning
    backgroundColor: '#fef9f0', // --warning-background
    duration: 10_000,
    getMessage: (storeLabel: string) =>
      `Un client s'apprête à faire une pré-autorisation dans "${storeLabel}".`,
  },
  cardMethodVerified: {
    color: '#6a9560', // --primary
    backgroundColor: '#f0fdf4', // --success-background
    duration: 10_000,
    getMessage: (storeLabel: string) => `Carte lue dans "${storeLabel}".`,
  },
  preAuthMethodVerified: {
    color: '#785700', // --warning
    backgroundColor: '#fef9f0', // --warning-background
    duration: 10_000,
    getMessage: (storeLabel: string) => `Pré-autorisation effectuée dans "${storeLabel}".`,
  },
} as const

const showKioskToast = (
  id: string,
  message: string,
  color: string,
  backgroundColor: string,
  duration: number,
  onAction?: () => void,
  actionLabel?: string,
) => {
  toast.custom(
    (toastId) => (
      <KioskToast
        toastId={toastId}
        message={message}
        color={color}
        backgroundColor={backgroundColor}
        onAction={onAction}
        actionLabel={actionLabel}
      />
    ),
    { id, duration },
  )
}

const KioskNotificationComponent = () => {
  const { pendingKiosks, openClientForPendingKiosk, askEntry } = useContext(ClientEntryContext)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleDoorOpen = useCallback(
    (data: WebSocketEventData) => {
      askEntry({
        kioskId: Number(data.kioskId) || 0,
        storeId: Number(data.storeId) || 0,
        storeLabel: data.storeLabel || `Boutique ${data.storeId}`,
      })
    },
    [askEntry],
  )

  useOpenDoorNotifications(handleDoorOpen)

  useEffect(() => {
    if (pendingKiosks.length > 0) {
      const kiosk = pendingKiosks[pendingKiosks.length - 1]
      const config = NOTIFICATION_CONFIGS.doorOpen
      showKioskToast(
        `door-open-${kiosk.kioskId}`,
        config.getMessage(kiosk.storeLabel || String(kiosk.storeId)),
        config.color,
        config.backgroundColor,
        config.duration,
        () => openClientForPendingKiosk(kiosk.kioskId, true),
        'Ouvrir',
      )
    }
  }, [pendingKiosks, openClientForPendingKiosk])

  const handleWsEvent = useCallback(
    (
      data: WebSocketEventData,
      configKey: keyof typeof NOTIFICATION_CONFIGS,
      options?: { withNavigation?: boolean; invalidateQuery?: boolean },
    ) => {
      const storeId = Number(data.storeId) || 0
      const storeLabel = data.storeLabel || `Boutique ${data.storeId}`
      const config = NOTIFICATION_CONFIGS[configKey]
      const onNavigate = options?.withNavigation
        ? () => navigate({ to: '/cameras', search: { storeId } })
        : undefined

      showKioskToast(
        `${configKey}-${crypto.randomUUID()}`,
        config.getMessage(storeLabel),
        config.color,
        config.backgroundColor,
        config.duration,
        onNavigate,
        onNavigate ? 'Voir' : undefined,
      )

      if (options?.invalidateQuery) {
        queryClient.invalidateQueries({ queryKey: ['pendingOpeningEvents', storeId] })
      }
    },
    [navigate, queryClient],
  )

  useCardMethodSelectedNotifications((data) =>
    handleWsEvent(data, 'cardMethodSelected', { withNavigation: true, invalidateQuery: true }),
  )
  usePreAuthorizationMethodSelectedNotifications((data) =>
    handleWsEvent(data, 'preAuthMethodSelected', { withNavigation: true, invalidateQuery: true }),
  )
  useCardMethodVerifiedNotifications((data) =>
    handleWsEvent(data, 'cardMethodVerified', { withNavigation: true, invalidateQuery: true }),
  )
  usePreAuthMethodVerifiedNotifications((data) =>
    handleWsEvent(data, 'preAuthMethodVerified', { withNavigation: true, invalidateQuery: true }),
  )

  return null
}

export default KioskNotificationComponent
